import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { Router } from '@angular/router';
import { AbstractMerchantSupplierComponent } from 'app/core/common/abstract.merchant-supplier.component';
import { Country } from 'app/core/models/country';
import { PointOfService } from 'app/core/models/pointOfService';
import { Supplier } from 'app/core/models/supplier';
import { MerchantService } from 'app/core/services/merchant.service';
import { NotifMessageService } from 'app/core/services/notifmessage.service';
import { PosService } from 'app/core/services/pos.service';
import { SupplierService } from 'app/core/services/supplier.service';
import { NGXLogger } from 'ngx-logger';
import { ReplaySubject, Subscription } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { AuthenticatedRouteGuard } from 'security/authenticatedRouteGuard.service';
import { KeycloakService } from 'security/keycloak.service';
import { tableEventChange } from '../table-utils/classes/tableChangeEvent';
import { actionType } from '../table-utils/enums/actionType';
import { columnType } from '../table-utils/enums/columnType';

@Component({
  selector: 'app-pos-page',
  templateUrl: './pos-page.component.html'
})
export class PosPageComponent extends AbstractMerchantSupplierComponent implements OnInit, OnDestroy {

  dataSubject$: ReplaySubject<any[]> = new ReplaySubject<any[]>();
  totalLength$: ReplaySubject<number> = new ReplaySubject<number>();

  isLoadingResults : boolean = true;
  isDeletedIncluded : boolean = false;

  statusItems: Array<String> = new Array<String>();

  columns: { 
    data : { 
      title: string; 
      type: columnType 
    }[],
    clickable : boolean;
    };

  pager : {page : number,
    sort : string,
    size : number
  } = {
    page: 0, 
    sort: "createdDate,DESC", 
    size: 20
  };

  criteria : PointOfService = new PointOfService();

  columnPropertyMapping = {
    "POS_NAME" : "name",
    "ID" : "id",
    "COUNTRY" : "address.country",
    "CITY" : "address.city",
    "TYPE" : "type",
    "ACTIONS" : "actions"
  };

  searchForm : FormGroup = new FormGroup({
    name: new FormControl(),
    id: new FormControl(),
    city: new FormControl(),
    country: new FormControl(),
    supplierId: new FormControl()
  });

  modalResponseSubscription : Subscription;

  isSearchByPOS: boolean = true;
  suppliers : Array<Supplier>;
  countryItems : Array<Country>;

  constructor(
    private roleGuard: AuthenticatedRouteGuard,
    private notifService: NotifMessageService,
    private keycloakService: KeycloakService,
    protected supplierService: SupplierService,
    protected merchantService: MerchantService,
    protected posService: PosService,
    protected log: NGXLogger,
    public router: Router,
  ) { super(supplierService, merchantService, posService, log, router); }


  formatPOS(pos : PointOfService) {
    const actions = [];

    if (this.roleGuard.isEnabled('POS_EDIT') && !pos.deleted) {
      actions.push({
        "name" : "EDIT",
        "type" : actionType.navigation,
        "value": `/service/pos/edit/${pos.id}`
      });
    }
    if (this.roleGuard.isEnabled('POS_READ')) {
      actions.push({
        "name" : "DETAILS",
        "type" : actionType.navigation,
        "value": `/service/pos/${pos.id}`
      });
    }
    if (this.roleGuard.isEnabled('POS_DELETE') && !pos.deleted) {
      actions.push({
        "name" : "DELETE",
        "type" : actionType.eventEmitter,
        "value": pos.id
      })
    }
    
    return {
      id: pos.id,
      POS_NAME: pos.name,
      ID: pos.id,
      COUNTRY: `COUNTRY_${pos.address.country}`,
      CITY: pos.address.city,
      TYPE: pos.type,
      deleted: pos.deleted,
      ACTIONS: actions
    }
  }

  ngOnInit() {
    this.columns = {
      data : [
      { title: "POS_NAME", type: columnType.text },
      { title: "ID", type: columnType.text },
      { title: "COUNTRY", type: columnType.translateText },
      { title: "CITY", type: columnType.text },
      { title: "TYPE", type: columnType.translateText },
      { title: "ACTIONS", type: columnType.actions }
    ],
    clickable : true
    };
    this.countryItems = this.getCountrys();
    this.supplierService.getAllSuppliers().subscribe(suppliers => {
      this.suppliers = suppliers.content;
    });

    this.keycloakService.rolesObservable.subscribe(roles => {
      if (roles.length !== 0) {
        if (this.roleGuard.isEnabled('POS_READ')) {
          this.loadData();
        } 
      }
    });
  }

  ngOnDestroy() {
    super.closeSubscribtions();
  }

  loadData(isLoadingSearch : boolean = false) : void {
    this.isLoadingResults = true;
    this.posService
    .findByCriteria(this.criteria, this.pager, this.isDeletedIncluded)
    .pipe(
      tap(allPos => this.totalLength$.next(allPos.totalElements)),
      map(allPos => {
        if (!allPos) {
          this.isLoadingResults = false;
          return [];
        }
        if (allPos.content.length === 0) {
          if (isLoadingSearch) {
            this.notifService.notifyInfo('EMPTY_RESULT');
          }
          return [];
        } 
        return allPos.content.map(supplier =>this.formatPOS(supplier));
      }),
    ).subscribe(allPos =>  {
      this.isLoadingResults = false;
      this.dataSubject$.next(allPos);
    })
  }

  deleteButton(id) {
    this.posService.getPosById(id).subscribe(pos => {
      console.log("Get by id", pos);
      this.notifService.emitModalWithDetails("DELETE_POS", [pos.name,"POS_DELETION","DELETE_POS_MSG"]);

      this.modalResponseSubscription = this.notifService.modalResponseSubject$.subscribe(resp => {
        console.log("Get resp", resp);
        if (resp) {
          this.posService.deletePos(id).subscribe(
            () => {
              this.loadData();
              this.notifService.notifySuccess('POS_DELETED');
              this.modalResponseSubscription.unsubscribe();
            }
          );
        } else {
          this.modalResponseSubscription.unsubscribe();
        }
      });
    });
  }

  includeDeleted(event : MatCheckboxChange) : void {
    this.isDeletedIncluded = event.checked;
    this.loadData();
  }

  tableChange(event: tableEventChange) {
    const sortProperty = this.columnPropertyMapping[event.sortEvent.active];
    const sortDirection = event.sortEvent.direction.toUpperCase();
    this.pager = {
      page: event.pageIndex, 
      sort: sortDirection !== "" && sortProperty ? [sortProperty, sortDirection].join(",") : "lastModifiedDate,DESC", 
      size: event.pageSize
    }
    this.loadData();
  }

  search() : void {
    this.criteria = new PointOfService();
    for (let field in this.searchForm.value) {
        if (!this.searchForm.value[field]) {
          delete this.searchForm.value[field];
        } else {
          if (field === "country") {
            this.criteria["address.country"] = this.searchForm.value[field];
          } else if (field === "city") {
            this.criteria["address.city"] = this.searchForm.value[field];
          } else {
            this.criteria[field] = this.searchForm.value[field];
          }
        }
    }
    this.loadData(true);
  }

  details(id : number) : void{
    this.router.navigate([`/service/pos/${id}`]);
  }

  clearSearch(): void {
    this.searchForm.reset();
    this.criteria = new PointOfService();
    this.loadData();
  }

}

